import dayjs from "dayjs";
import "dayjs/locale/hy-am";
import "dayjs/locale/ru";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en.json";
import hy from "./hy.json";
import ru from "./ru.json";

export const LOCALES = ["hy", "ru", "en"] as const;
export type UiLocale = (typeof LOCALES)[number];
const STORAGE_KEY = "audit.locale";

/** dayjs / Mantine Dates locale for a UI language. */
export const DAYJS_LOCALE: Record<UiLocale, string> = { hy: "hy-am", ru: "ru", en: "en" };
/** Native names, shown in the language menu and on the settings page. */
export const LOCALE_LABEL: Record<UiLocale, string> = {
  hy: "Հայերեն",
  ru: "Русский",
  en: "English",
};

function savedLocale(): UiLocale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && (LOCALES as readonly string[]).includes(v)) return v as UiLocale;
  } catch {
    // storage may be unavailable (private mode); Armenian is the default anyway
  }
  return "hy";
}

i18next.use(initReactI18next).init({
  resources: { hy: { translation: hy }, ru: { translation: ru }, en: { translation: en } },
  lng: savedLocale(),
  fallbackLng: "hy",
  interpolation: { escapeValue: false },
});

export function hasSavedLocale(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

function applyLocale(locale: UiLocale): void {
  dayjs.locale(DAYJS_LOCALE[locale]);
  document.documentElement.lang = locale;
}

/** User's explicit choice: remembered in this browser. */
export function setLocale(locale: UiLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore
  }
  void i18next.changeLanguage(locale);
  applyLocale(locale);
}

/** Profile default (users.locale): applied only when the browser has no explicit choice. */
export function applyProfileLocale(locale: string): void {
  if (hasSavedLocale() || !(LOCALES as readonly string[]).includes(locale)) return;
  if (locale === i18next.language) return;
  void i18next.changeLanguage(locale);
  applyLocale(locale as UiLocale);
}

applyLocale(i18next.language as UiLocale);

export default i18next;
